import { v4 as uuidv4 } from 'uuid';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { currentUserShowSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { storableError } from '../../util/errors';
import { simplifyRouteLineString } from '../../util/routePlanner/corridor';
import { fetchDirections } from '../../util/routePlanner/directions';
import { buildSdkFilterParams, cleanFilterParams } from '../../util/routePlanner/filterBridge';
import {
  getPolyline as readPolylineCache,
  removePolyline as removePolylineCache,
  setPolyline as writePolylineCache,
} from '../../util/routePlanner/polylineCache';
import {
  canFitInProfile,
  CURRENT_SCHEMA_VERSION,
  DEFAULT_ROUTE_PREFERENCES as SCHEMA_DEFAULT_ROUTE_PREFERENCES,
  MAX_SAVED_ROUTES,
  migrateRoute,
  validateRoute,
} from '../../util/routePlanner/schema';
import { interleaveAndDedupeListings } from '../../util/routePlanner/tiling';
import { decodeRouteFromParam } from '../../util/routePlanner/shareLink';
import { BOOKED_TRANSITIONS, buildBookedMap } from '../../util/routePlanner/transactions';

// ================ Constants ================ //

// Max total waypoints: start + middles + end. Mapbox Directions API caps at
// 25 coords per request, but 12 keeps the UI manageable and the polyline
// cache within the 50 KB profile budget.
export const MAX_WAYPOINTS = 12;

// MUI slider bounds. Default is the mid-range value from the plan: wide enough
// to show a meaningful set of listings on most European routes without
// flooding the map, narrow enough that the corridor polygon is still readable.
export const MIN_CORRIDOR_WIDTH_KM = 5;
export const MAX_CORRIDOR_WIDTH_KM = 50;
export const DEFAULT_CORRIDOR_WIDTH_KM = 15;
export const DEFAULT_ROUTE_PREFERENCES = SCHEMA_DEFAULT_ROUTE_PREFERENCES;

// Hard ceiling on the total listing count shown on the map at once. Users
// zoom in to reveal more — the cap keeps the list legible and the map
// un-crowded. Sharetribe's per-page cap is 100 so the two are now the same.
export const LISTINGS_HARD_CAP = 100;

// Number of tiles to fan the bbox across when the whole route is visible,
// so each leg of a long road gets its share of the 100-listing budget.
const ROUTE_SAMPLE_TILES = 5;

// Per-tile page size for the SDK query. When the whole route fits in the
// viewport we fan out and divide the budget across tiles; when zoomed in
// we run one query that fills the entire 100-result budget.
const LISTINGS_PER_TILE = 100;

// Sparse fieldset for the corridor query. Mirrors SearchPage.duck.js's
// loadData but keeps the payload small — we only need enough to render a
// listing card and compute the corridor filter, not the full detail page.
const LISTINGS_QUERY_FIELDS = {
  include: ['images'],
  'fields.listing': [
    'title',
    'geolocation',
    'price',
    'createdAt',
    'publicData.category',
    'publicData.locationPlace',
    'publicData.verified',
    'metadata.rating',
    'metadata.ratingCount',
    'metadata.explorerListing',
    'metadata.annualPassPrice',
  ],
  'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
  'limit.images': 1,
  pub_hidden: false,
};

// ================ Action types ================ //

const SET_MODE = 'app/RoutePlannerPage/SET_MODE';

const SET_START_WAYPOINT = 'app/RoutePlannerPage/SET_START_WAYPOINT';
const SET_END_WAYPOINT = 'app/RoutePlannerPage/SET_END_WAYPOINT';
const ADD_MIDDLE_WAYPOINT = 'app/RoutePlannerPage/ADD_MIDDLE_WAYPOINT';
const UPDATE_MIDDLE_WAYPOINT = 'app/RoutePlannerPage/UPDATE_MIDDLE_WAYPOINT';
const REMOVE_MIDDLE_WAYPOINT = 'app/RoutePlannerPage/REMOVE_MIDDLE_WAYPOINT';
const REORDER_MIDDLE_WAYPOINTS = 'app/RoutePlannerPage/REORDER_MIDDLE_WAYPOINTS';
const SET_ALL_WAYPOINTS = 'app/RoutePlannerPage/SET_ALL_WAYPOINTS';

const FETCH_ROUTE_REQUEST = 'app/RoutePlannerPage/FETCH_ROUTE_REQUEST';
const FETCH_ROUTE_SUCCESS = 'app/RoutePlannerPage/FETCH_ROUTE_SUCCESS';
const FETCH_ROUTE_ERROR = 'app/RoutePlannerPage/FETCH_ROUTE_ERROR';
const CLEAR_ROUTE_GEOMETRY = 'app/RoutePlannerPage/CLEAR_ROUTE_GEOMETRY';

const SET_CORRIDOR_WIDTH = 'app/RoutePlannerPage/SET_CORRIDOR_WIDTH';
const SET_ROUTE_PREFERENCE = 'app/RoutePlannerPage/SET_ROUTE_PREFERENCE';
const SET_MAP_BOUNDS = 'app/RoutePlannerPage/SET_MAP_BOUNDS';

const FETCH_LISTINGS_REQUEST = 'app/RoutePlannerPage/FETCH_LISTINGS_REQUEST';
const FETCH_LISTINGS_SUCCESS = 'app/RoutePlannerPage/FETCH_LISTINGS_SUCCESS';
const FETCH_LISTINGS_ERROR = 'app/RoutePlannerPage/FETCH_LISTINGS_ERROR';
const CLEAR_LISTINGS = 'app/RoutePlannerPage/CLEAR_LISTINGS';

const UPDATE_FILTERS = 'app/RoutePlannerPage/UPDATE_FILTERS';
const RESET_FILTERS = 'app/RoutePlannerPage/RESET_FILTERS';

const ADD_ITINERARY_STOP = 'app/RoutePlannerPage/ADD_ITINERARY_STOP';
const UPDATE_ITINERARY_STOP = 'app/RoutePlannerPage/UPDATE_ITINERARY_STOP';
const REMOVE_ITINERARY_STOP = 'app/RoutePlannerPage/REMOVE_ITINERARY_STOP';
const REORDER_ITINERARY = 'app/RoutePlannerPage/REORDER_ITINERARY';
const CLEAR_ITINERARY = 'app/RoutePlannerPage/CLEAR_ITINERARY';

const SET_SAVED_ROUTES = 'app/RoutePlannerPage/SET_SAVED_ROUTES';
const SET_CURRENT_ROUTE_ID = 'app/RoutePlannerPage/SET_CURRENT_ROUTE_ID';
const SAVE_ROUTE_REQUEST = 'app/RoutePlannerPage/SAVE_ROUTE_REQUEST';
const SAVE_ROUTE_SUCCESS = 'app/RoutePlannerPage/SAVE_ROUTE_SUCCESS';
const SAVE_ROUTE_ERROR = 'app/RoutePlannerPage/SAVE_ROUTE_ERROR';
const RESET_WORKING_STATE = 'app/RoutePlannerPage/RESET_WORKING_STATE';
const OPEN_DRAFT_MIGRATION_DIALOG = 'app/RoutePlannerPage/OPEN_DRAFT_MIGRATION_DIALOG';
const CLOSE_DRAFT_MIGRATION_DIALOG = 'app/RoutePlannerPage/CLOSE_DRAFT_MIGRATION_DIALOG';

const SET_SHARED_ROUTE_NAME = 'app/RoutePlannerPage/SET_SHARED_ROUTE_NAME';
const SET_SHARED_ROUTE_ERROR = 'app/RoutePlannerPage/SET_SHARED_ROUTE_ERROR';

const FETCH_BOOKED_REQUEST = 'app/RoutePlannerPage/FETCH_BOOKED_REQUEST';
const FETCH_BOOKED_SUCCESS = 'app/RoutePlannerPage/FETCH_BOOKED_SUCCESS';
const FETCH_BOOKED_ERROR = 'app/RoutePlannerPage/FETCH_BOOKED_ERROR';

// ================ Helpers ================ //

const clampCorridorWidth = (km) => {
  const n = Number(km);
  if (!Number.isFinite(n)) return DEFAULT_CORRIDOR_WIDTH_KM;
  if (n < MIN_CORRIDOR_WIDTH_KM) return MIN_CORRIDOR_WIDTH_KM;
  if (n > MAX_CORRIDOR_WIDTH_KM) return MAX_CORRIDOR_WIDTH_KM;
  return n;
};

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.RoutePlannerPage} */
const initialState = {
  mode: 'plan',
  startWaypoint: null,
  endWaypoint: null,
  middleWaypoints: [],
  routeGeoJSON: null,
  totalDistanceKm: null,
  totalDurationMin: null,
  fetchRouteInProgress: false,
  fetchRouteError: null,
  corridorWidthKm: DEFAULT_CORRIDOR_WIDTH_KM,
  routePreferences: DEFAULT_ROUTE_PREFERENCES,
  filterParams: {},
  listingIds: [],
  fetchListingsInProgress: false,
  fetchListingsError: null,
  listingsCapHit: false,
  itinerary: [],
  savedRoutes: [],
  currentRouteId: null,
  sharedRouteName: null,
  sharedRouteError: false,
  saveInProgress: false,
  saveError: null,
  draftMigrationDialogOpen: false,
  bookedListingMap: {},
  fetchBookedInProgress: false,
  fetchBookedError: null,
  mapBounds: null,
};

const clearedListings = {
  listingIds: [],
  fetchListingsInProgress: false,
  fetchListingsError: null,
  listingsCapHit: false,
};

const clearedGeometry = {
  routeGeoJSON: null,
  totalDistanceKm: null,
  totalDurationMin: null,
  fetchRouteError: null,
};

/**
 * Count the filled slots (start + middles + end). If dropping below 2,
 * geometry + listings are no longer meaningful and must be cleared.
 */
const countFilled = (state) => {
  let n = state.middleWaypoints.length;
  if (state.startWaypoint) n += 1;
  if (state.endWaypoint) n += 1;
  return n;
};

const reducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_MODE:
      return { ...state, mode: payload };

    case SET_START_WAYPOINT: {
      const next = { ...state, startWaypoint: payload };
      // If we just dropped below a 2-point route, clear the drawn line and
      // the listings — they refer to the previous geometry.
      if (countFilled(next) < 2) {
        return { ...next, ...clearedGeometry, ...clearedListings };
      }
      return next;
    }

    case SET_END_WAYPOINT: {
      const next = { ...state, endWaypoint: payload };
      if (countFilled(next) < 2) {
        return { ...next, ...clearedGeometry, ...clearedListings };
      }
      return next;
    }

    case ADD_MIDDLE_WAYPOINT: {
      if (countFilled(state) >= MAX_WAYPOINTS) {
        return state;
      }
      return {
        ...state,
        middleWaypoints: [...state.middleWaypoints, payload],
      };
    }

    case UPDATE_MIDDLE_WAYPOINT: {
      const { id, patch } = payload;
      return {
        ...state,
        middleWaypoints: state.middleWaypoints.map((wp) =>
          wp.id === id ? { ...wp, ...patch } : wp,
        ),
      };
    }

    case REMOVE_MIDDLE_WAYPOINT: {
      const next = {
        ...state,
        middleWaypoints: state.middleWaypoints.filter((wp) => wp.id !== payload),
      };
      if (countFilled(next) < 2) {
        return { ...next, ...clearedGeometry, ...clearedListings };
      }
      return next;
    }

    case SET_ALL_WAYPOINTS: {
      const { startWaypoint, middleWaypoints, endWaypoint } = payload;
      return {
        ...state,
        startWaypoint: startWaypoint || null,
        middleWaypoints: Array.isArray(middleWaypoints) ? middleWaypoints : [],
        endWaypoint: endWaypoint || null,
        // The incoming geometry is stale for the new waypoint set.
        ...clearedGeometry,
        ...clearedListings,
      };
    }

    case REORDER_MIDDLE_WAYPOINTS: {
      const { fromIndex, toIndex } = payload;
      const list = state.middleWaypoints;
      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= list.length ||
        toIndex >= list.length
      ) {
        return state;
      }
      const next = list.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return { ...state, middleWaypoints: next };
    }

    case FETCH_ROUTE_REQUEST:
      return { ...state, fetchRouteInProgress: true, fetchRouteError: null };

    case FETCH_ROUTE_SUCCESS:
      return {
        ...state,
        fetchRouteInProgress: false,
        fetchRouteError: null,
        routeGeoJSON: payload.routeGeoJSON,
        totalDistanceKm: payload.totalDistanceKm,
        totalDurationMin: payload.totalDurationMin,
      };

    case FETCH_ROUTE_ERROR:
      return { ...state, fetchRouteInProgress: false, fetchRouteError: payload };

    case CLEAR_ROUTE_GEOMETRY:
      return { ...state, ...clearedGeometry, ...clearedListings };

    case SET_CORRIDOR_WIDTH:
      return { ...state, corridorWidthKm: clampCorridorWidth(payload) };

    case SET_ROUTE_PREFERENCE:
      return {
        ...state,
        routePreferences: {
          ...DEFAULT_ROUTE_PREFERENCES,
          ...state.routePreferences,
          [payload.key]: Boolean(payload.value),
        },
      };

    case SET_MAP_BOUNDS:
      return { ...state, mapBounds: payload };

    case FETCH_LISTINGS_REQUEST:
      return { ...state, fetchListingsInProgress: true, fetchListingsError: null };

    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        fetchListingsInProgress: false,
        fetchListingsError: null,
        listingIds: payload.listingIds,
        listingsCapHit: payload.capHit,
      };

    case FETCH_LISTINGS_ERROR:
      return { ...state, fetchListingsInProgress: false, fetchListingsError: payload };

    case CLEAR_LISTINGS:
      return { ...state, ...clearedListings };

    case UPDATE_FILTERS:
      // Merge the patch into the current filter bag, then strip empties. A
      // FilterComponent clears a filter by submitting `{ key: null }`, and we
      // want that to drop the key entirely so the active-filter count stays
      // accurate and the SDK query doesn't carry stale `?foo=null`.
      return {
        ...state,
        filterParams: cleanFilterParams({ ...state.filterParams, ...payload }),
      };

    case RESET_FILTERS:
      return { ...state, filterParams: {} };

    case ADD_ITINERARY_STOP:
      return { ...state, itinerary: [...state.itinerary, payload] };

    case UPDATE_ITINERARY_STOP: {
      const { stopId, patch } = payload;
      return {
        ...state,
        itinerary: state.itinerary.map((stop) =>
          stop.stopId === stopId ? { ...stop, ...patch } : stop,
        ),
      };
    }

    case REMOVE_ITINERARY_STOP:
      return {
        ...state,
        itinerary: state.itinerary.filter((stop) => stop.stopId !== payload),
      };

    case REORDER_ITINERARY: {
      const { fromIndex, toIndex } = payload;
      const list = state.itinerary;
      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= list.length ||
        toIndex >= list.length
      ) {
        return state;
      }
      const next = list.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return { ...state, itinerary: next };
    }

    case CLEAR_ITINERARY:
      return { ...state, itinerary: [] };

    case SET_SAVED_ROUTES:
      return { ...state, savedRoutes: payload };

    case SET_CURRENT_ROUTE_ID:
      return { ...state, currentRouteId: payload };

    case SET_SHARED_ROUTE_NAME:
      return { ...state, sharedRouteName: payload };

    case SET_SHARED_ROUTE_ERROR:
      return { ...state, sharedRouteError: Boolean(payload) };

    case SAVE_ROUTE_REQUEST:
      return { ...state, saveInProgress: true, saveError: null };

    case SAVE_ROUTE_SUCCESS:
      return { ...state, saveInProgress: false, saveError: null };

    case SAVE_ROUTE_ERROR:
      return { ...state, saveInProgress: false, saveError: payload };

    case RESET_WORKING_STATE:
      // Keep savedRoutes + the saveError surface so the user sees any pending
      // warning after discarding a draft; clear everything that scopes to the
      // current working route.
      return {
        ...initialState,
        savedRoutes: state.savedRoutes,
        draftMigrationDialogOpen: state.draftMigrationDialogOpen,
      };

    case OPEN_DRAFT_MIGRATION_DIALOG:
      return { ...state, draftMigrationDialogOpen: true };

    case CLOSE_DRAFT_MIGRATION_DIALOG:
      return { ...state, draftMigrationDialogOpen: false };

    case FETCH_BOOKED_REQUEST:
      return { ...state, fetchBookedInProgress: true, fetchBookedError: null };

    case FETCH_BOOKED_SUCCESS:
      return {
        ...state,
        fetchBookedInProgress: false,
        fetchBookedError: null,
        bookedListingMap: payload,
      };

    case FETCH_BOOKED_ERROR:
      return { ...state, fetchBookedInProgress: false, fetchBookedError: payload };

    default:
      return state;
  }
};

export default reducer;

// ================ Selectors ================ //

/**
 * Build the ordered waypoint array consumed by the Directions API, the map
 * marker renderer, and the saved-route serializer: `[start, ...middles, end]`
 * with nulls filtered out.
 *
 * @param {import('../../../types/vansite').VanSite.State.RoutePlannerPage} state
 * @returns {import('../../../types/vansite').VanSite.RoutePlanner.Waypoint[]}
 */
export const selectOrderedWaypoints = (state) => {
  const result = [];
  if (state.startWaypoint) result.push(state.startWaypoint);
  for (const wp of state.middleWaypoints) result.push(wp);
  if (state.endWaypoint) result.push(state.endWaypoint);
  return result;
};

export const selectRoutePreferences = (state) => ({
  ...DEFAULT_ROUTE_PREFERENCES,
  avoidMotorway: Boolean(state.routePreferences?.avoidMotorway),
  avoidTolls: Boolean(state.routePreferences?.avoidTolls),
});

/**
 * Build a route-like object for the current working trip, suitable for
 * `encodeRouteToParam`. No id/timestamps — those are minted on save.
 *
 * @param {import('../../../types/vansite').VanSite.State.RoutePlannerPage} state
 */
export const selectShareableRoute = (state) => ({
  schemaVersion: CURRENT_SCHEMA_VERSION,
  name: '',
  corridorWidthKm: state.corridorWidthKm,
  routePreferences: selectRoutePreferences(state),
  waypoints: selectOrderedWaypoints(state).map(({ id, label, lat, lng }) => ({
    id,
    label,
    lat,
    lng,
  })),
  itinerary: (state.itinerary || []).map((stop) => ({
    stopId: stop.stopId,
    listingId: stop.listingId,
    startDate: stop.startDate || null,
    endDate: stop.endDate || null,
  })),
});

// ================ Action creators ================ //

export const setMode = (mode) => ({ type: SET_MODE, payload: mode });

export const setStartWaypoint = (waypoint) => ({ type: SET_START_WAYPOINT, payload: waypoint });

export const setEndWaypoint = (waypoint) => ({ type: SET_END_WAYPOINT, payload: waypoint });

export const addMiddleWaypoint = (waypoint) => ({
  type: ADD_MIDDLE_WAYPOINT,
  payload: waypoint,
});

export const updateMiddleWaypoint = (id, patch) => ({
  type: UPDATE_MIDDLE_WAYPOINT,
  payload: { id, patch },
});

export const removeMiddleWaypoint = (id) => ({ type: REMOVE_MIDDLE_WAYPOINT, payload: id });

export const reorderMiddleWaypoints = (fromIndex, toIndex) => ({
  type: REORDER_MIDDLE_WAYPOINTS,
  payload: { fromIndex, toIndex },
});

export const setAllWaypoints = ({ startWaypoint, middleWaypoints, endWaypoint }) => ({
  type: SET_ALL_WAYPOINTS,
  payload: { startWaypoint, middleWaypoints, endWaypoint },
});

export const fetchRouteRequest = () => ({ type: FETCH_ROUTE_REQUEST });

export const fetchRouteSuccess = (routeGeoJSON, totalDistanceKm, totalDurationMin) => ({
  type: FETCH_ROUTE_SUCCESS,
  payload: { routeGeoJSON, totalDistanceKm, totalDurationMin },
});

export const fetchRouteError = (error) => ({ type: FETCH_ROUTE_ERROR, payload: error });

export const clearRouteGeometry = () => ({ type: CLEAR_ROUTE_GEOMETRY });

export const setCorridorWidth = (km) => ({ type: SET_CORRIDOR_WIDTH, payload: km });

export const setRoutePreference = (key, value) => ({
  type: SET_ROUTE_PREFERENCE,
  payload: { key, value },
});

export const setMapBounds = (bounds) => ({ type: SET_MAP_BOUNDS, payload: bounds });

export const fetchListingsRequest = () => ({ type: FETCH_LISTINGS_REQUEST });

export const fetchListingsSuccess = (listingIds, capHit) => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { listingIds, capHit },
});

export const fetchListingsError = (error) => ({ type: FETCH_LISTINGS_ERROR, payload: error });

export const clearListings = () => ({ type: CLEAR_LISTINGS });

export const updateFilters = (patch) => ({ type: UPDATE_FILTERS, payload: patch });

export const resetFilters = () => ({ type: RESET_FILTERS });

export const addItineraryStop = (stop) => ({ type: ADD_ITINERARY_STOP, payload: stop });

export const updateItineraryStop = (stopId, patch) => ({
  type: UPDATE_ITINERARY_STOP,
  payload: { stopId, patch },
});

export const removeItineraryStop = (stopId) => ({
  type: REMOVE_ITINERARY_STOP,
  payload: stopId,
});

export const reorderItinerary = (fromIndex, toIndex) => ({
  type: REORDER_ITINERARY,
  payload: { fromIndex, toIndex },
});

export const clearItinerary = () => ({ type: CLEAR_ITINERARY });

export const setSavedRoutes = (routes) => ({ type: SET_SAVED_ROUTES, payload: routes });

export const setCurrentRouteId = (id) => ({ type: SET_CURRENT_ROUTE_ID, payload: id });

export const setSharedRouteName = (name) => ({ type: SET_SHARED_ROUTE_NAME, payload: name });

export const setSharedRouteError = (value) => ({
  type: SET_SHARED_ROUTE_ERROR,
  payload: Boolean(value),
});

export const saveRouteRequest = () => ({ type: SAVE_ROUTE_REQUEST });

export const saveRouteSuccess = () => ({ type: SAVE_ROUTE_SUCCESS });

export const saveRouteError = (error) => ({ type: SAVE_ROUTE_ERROR, payload: error });

export const resetWorkingState = () => ({ type: RESET_WORKING_STATE });

export const openDraftMigrationDialog = () => ({ type: OPEN_DRAFT_MIGRATION_DIALOG });

export const closeDraftMigrationDialog = () => ({ type: CLOSE_DRAFT_MIGRATION_DIALOG });

export const fetchBookedRequest = () => ({ type: FETCH_BOOKED_REQUEST });

export const fetchBookedSuccess = (bookedListingMap) => ({
  type: FETCH_BOOKED_SUCCESS,
  payload: bookedListingMap,
});

export const fetchBookedError = (error) => ({ type: FETCH_BOOKED_ERROR, payload: error });

// ================ Thunks ================ //

/**
 * Fetch the driving route for the current start + middles + end.
 *
 * The caller (useMapboxDirections hook) owns debouncing and abort. It builds
 * an AbortController and passes its `signal` to this thunk so a stale fetch
 * can be cancelled mid-flight. Abort errors are swallowed silently because
 * they are expected and not user-facing.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {import('../../../types/vansite').AppThunkAction<Promise<void>>}
 */
export const fetchRoute = ({ signal } = {}) => async (dispatch, getState) => {
  const ordered = selectOrderedWaypoints(getState().RoutePlannerPage);
  if (ordered.length < 2) {
    return;
  }

  dispatch(fetchRouteRequest());
  try {
    const preferences = selectRoutePreferences(getState().RoutePlannerPage);
    const { geometry, distanceMeters, durationSeconds } = await fetchDirections({
      waypoints: ordered.map(({ lng, lat }) => ({ lng, lat })),
      preferences,
      signal,
    });
    if (signal?.aborted) {
      return;
    }
    dispatch(
      fetchRouteSuccess(geometry, distanceMeters / 1000, Math.round(durationSeconds / 60)),
    );
  } catch (error) {
    if (error?.name === 'AbortError' || signal?.aborted) {
      return;
    }
    dispatch(fetchRouteError(storableError(error)));
  }
};

/**
 * Sharetribe expects `bounds` as `"lat_ne,lng_ne,lat_sw,lng_sw"`.
 */
const boundsString = ([minLng, minLat, maxLng, maxLat]) =>
  `${maxLat},${maxLng},${minLat},${minLng}`;

const bboxFromMapBounds = (bounds) => {
  if (!bounds) return null;
  const { ne, sw } = bounds;
  if (!ne || !sw) return null;
  return [sw.lng, sw.lat, ne.lng, ne.lat];
};

/**
 * Bbox of a LineString in [minLng, minLat, maxLng, maxLat] form.
 */
const bboxFromLineString = (lineString) => {
  const coords = lineString?.coordinates;
  if (!Array.isArray(coords) || coords.length === 0) return null;
  let minLng = coords[0][0];
  let minLat = coords[0][1];
  let maxLng = coords[0][0];
  let maxLat = coords[0][1];
  for (let i = 1; i < coords.length; i += 1) {
    const [lng, lat] = coords[i];
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return [minLng, minLat, maxLng, maxLat];
};

const bboxContains = (outer, inner) => {
  if (!outer || !inner) return false;
  return (
    inner[0] >= outer[0] &&
    inner[1] >= outer[1] &&
    inner[2] <= outer[2] &&
    inner[3] <= outer[3]
  );
};

/**
 * Slice a route LineString into `segments` even-sized coordinate chunks and
 * return a bbox for each chunk. Used when the entire route is visible and
 * we want to spread the 100-result budget along its length.
 */
const sampleRouteBBoxes = (lineString, segments) => {
  const coords = lineString?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2 || segments < 1) return [];
  const n = Math.min(segments, coords.length - 1);
  const chunkSize = Math.ceil(coords.length / n);
  const bboxes = [];
  for (let i = 0; i < n; i += 1) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize + 1, coords.length);
    const slice = coords.slice(start, end);
    if (slice.length < 2) continue;
    let minLng = slice[0][0];
    let minLat = slice[0][1];
    let maxLng = slice[0][0];
    let maxLat = slice[0][1];
    for (let j = 1; j < slice.length; j += 1) {
      const [lng, lat] = slice[j];
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    // Tiny buffer so a single-direction segment still has non-zero area.
    const dLng = Math.max(maxLng - minLng, 0.02);
    const dLat = Math.max(maxLat - minLat, 0.02);
    const bufferLng = dLng * 0.1;
    const bufferLat = dLat * 0.1;
    bboxes.push([
      minLng - bufferLng,
      minLat - bufferLat,
      maxLng + bufferLng,
      maxLat + bufferLat,
    ]);
  }
  return bboxes;
};

/**
 * Fetch listings for the current map viewport — or, when the whole route
 * is visible in that viewport, along the route split into
 * `ROUTE_SAMPLE_TILES` segments. Always capped at `LISTINGS_HARD_CAP`
 * results total.
 *
 * When there is no route yet, just fetches the raw viewport. When there
 * is a route but no map bounds (first fetch before a moveend fires), falls
 * back to the route's bbox so the map isn't empty.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {import('../../../types/vansite').AppThunkAction<Promise<void>>}
 */
export const fetchCorridorListings = ({ signal } = {}) => async (dispatch, getState, sdk) => {
  const state = getState().RoutePlannerPage;
  const routeGeoJSON = state.routeGeoJSON;
  const mapBboxRaw = bboxFromMapBounds(state.mapBounds);
  const routeBbox = routeGeoJSON ? bboxFromLineString(routeGeoJSON) : null;

  // Nothing to fetch against: no viewport yet AND no route.
  if (!mapBboxRaw && !routeBbox) {
    dispatch(clearListings());
    return;
  }

  dispatch(fetchListingsRequest());

  const filterSdkParams = buildSdkFilterParams(state.filterParams);

  // Decide query bboxes:
  //  - route present and fully visible → sample N segments along route
  //  - route present, viewport tighter than route → query just the viewport
  //  - no route → query just the viewport
  //  - viewport missing (first fetch) → fall back to route bbox
  let queryBboxes;
  let perPage;
  if (routeBbox && mapBboxRaw && bboxContains(mapBboxRaw, routeBbox)) {
    queryBboxes = sampleRouteBBoxes(
      simplifyRouteLineString(routeGeoJSON),
      ROUTE_SAMPLE_TILES,
    );
    // Each tile fetches its full page; interleaveAndDedupeListings below
    // shares the hard cap fairly across segments. An equal-share-per-tile
    // perPage (100 / N) starves dense segments — on a long route a single
    // segment can easily hold more than N-th of the cap, and Sharetribe's
    // default ordering silently drops the overflow.
    perPage = LISTINGS_PER_TILE;
  } else if (mapBboxRaw) {
    queryBboxes = [mapBboxRaw];
    perPage = LISTINGS_PER_TILE;
  } else {
    queryBboxes = [routeBbox];
    perPage = LISTINGS_PER_TILE;
  }

  try {
    const responses = await Promise.all(
      queryBboxes.map((bbox) =>
        sdk.listings.query({
          bounds: boundsString(bbox),
          per_page: perPage,
          ...LISTINGS_QUERY_FIELDS,
          ...filterSdkParams,
        }),
      ),
    );
    if (signal?.aborted) return;

    for (const response of responses) {
      dispatch(addMarketplaceEntities(response));
    }

    const listingArrays = responses.map((response) => response?.data?.data ?? []);
    const merged = interleaveAndDedupeListings(listingArrays);
    const capHit = merged.length > LISTINGS_HARD_CAP;
    const capped = capHit ? merged.slice(0, LISTINGS_HARD_CAP) : merged;
    const ids = capped.map((listing) => listing.id);

    dispatch(fetchListingsSuccess(ids, capHit));
  } catch (error) {
    if (error?.name === 'AbortError' || signal?.aborted) return;
    dispatch(fetchListingsError(storableError(error)));
  }
};

/**
 * Pull saved routes off `currentUser.privateData.savedRoutes`, migrate each
 * through the schema to be tolerant of older shapes, and mirror the result
 * into Redux. Called after `fetchCurrentUser` completes so the menu stays in
 * sync with the server — Redux is the read source for UI, Sharetribe is the
 * source of truth.
 */
export const hydrateSavedRoutes = () => (dispatch, getState) => {
  const currentUser = getState().user.currentUser;
  const raw = currentUser?.attributes?.profile?.privateData?.savedRoutes;
  if (!Array.isArray(raw)) {
    dispatch(setSavedRoutes([]));
    return;
  }
  const migrated = raw.map(migrateRoute).filter(Boolean);
  dispatch(setSavedRoutes(migrated));
};

/**
 * Build the route object we're about to persist. Strips polyline / distance /
 * duration — those live in polylineCache, never in privateData. Keeps the
 * existing `id` when updating a loaded route.
 */
const buildSavedRoute = (state, name, id) => {
  const waypoints = selectOrderedWaypoints(state).map(({ id: wpId, label, lat, lng }) => ({
    id: wpId,
    label,
    lat,
    lng,
  }));
  const now = Date.now();
  const existing = id ? state.savedRoutes.find((r) => r.id === id) : null;
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: id || uuidv4(),
    name: name.trim(),
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    corridorWidthKm: state.corridorWidthKm,
    routePreferences: selectRoutePreferences(state),
    waypoints,
    itinerary: state.itinerary.map((stop) => ({
      stopId: stop.stopId,
      listingId: stop.listingId,
      startDate: stop.startDate || null,
      endDate: stop.endDate || null,
    })),
  };
};

/**
 * Persist the current working route to `currentUser.privateData.savedRoutes`.
 *
 * The `id` argument is optional — omit for a new route, pass to overwrite an
 * existing one. Enforces the 5-route cap (unless we're updating an existing
 * route) and the size budget via `canFitInProfile`. On success writes the
 * current geometry to the local polylineCache so the next `loadSavedRoute`
 * hydrates without a Directions call.
 *
 * @param {string} name
 * @param {string} [id]
 * @returns {import('../../../types/vansite').AppThunkAction<Promise<import('../../../types/vansite').VanSite.RoutePlanner.SavedRoute | null>>}
 */
export const saveCurrentRoute = (name, id) => async (dispatch, getState, sdk) => {
  const state = getState().RoutePlannerPage;
  const isAuthenticated = getState().auth?.isAuthenticated;
  if (!isAuthenticated) {
    dispatch(saveRouteError({ message: 'RoutePlannerPage.saveError.signInRequired' }));
    return null;
  }

  const route = buildSavedRoute(state, name, id);
  const validation = validateRoute(route);
  if (validation) {
    dispatch(saveRouteError({ message: validation }));
    return null;
  }

  const existingRoutes = state.savedRoutes || [];
  const isUpdate = existingRoutes.some((r) => r.id === route.id);
  if (!isUpdate && existingRoutes.length >= MAX_SAVED_ROUTES) {
    dispatch(saveRouteError({ message: 'RoutePlannerPage.saveError.tooManyRoutes' }));
    return null;
  }
  if (!canFitInProfile(existingRoutes, route)) {
    dispatch(saveRouteError({ message: 'RoutePlannerPage.saveError.tooLarge' }));
    return null;
  }

  const nextRoutes = isUpdate
    ? existingRoutes.map((r) => (r.id === route.id ? route : r))
    : [...existingRoutes, route];

  dispatch(saveRouteRequest());
  try {
    const response = await sdk.currentUser.updateProfile(
      { privateData: { savedRoutes: nextRoutes } },
      { expand: true, include: ['profileImage'] },
    );
    const updatedUser = response?.data?.data;
    if (updatedUser) {
      dispatch(currentUserShowSuccess(updatedUser));
    }
    dispatch(setSavedRoutes(nextRoutes));
    dispatch(setCurrentRouteId(route.id));
    dispatch(saveRouteSuccess());

    // Cache the just-fetched geometry under the route id so loading this
    // route on the same device is instant and pays zero Directions calls.
    if (state.routeGeoJSON) {
      writePolylineCache(route.id, {
        routeGeoJSON: state.routeGeoJSON,
        totalDistanceKm: state.totalDistanceKm,
        totalDurationMin: state.totalDurationMin,
      });
    }
    return route;
  } catch (error) {
    dispatch(saveRouteError(storableError(error)));
    return null;
  }
};

/**
 * Hydrate Redux from a saved route, then kick off the geometry + listings
 * refetch that the page normally does when the user edits waypoints.
 *
 * Polyline cache is consulted first — on hit we bypass Directions entirely
 * and dispatch the cached geometry; on miss the `useMapboxDirections` hook
 * picks up the new waypoints and fires a fresh request, and we write the
 * result back to the cache once it lands.
 */
export const loadSavedRoute = (routeId) => (dispatch, getState) => {
  const state = getState().RoutePlannerPage;
  const saved = state.savedRoutes.find((r) => r.id === routeId);
  if (!saved) return;

  // Rebuild the slot-model shape from the flat waypoint array.
  const waypoints = saved.waypoints || [];
  const startWaypoint = waypoints[0] || null;
  const endWaypoint = waypoints.length >= 2 ? waypoints[waypoints.length - 1] : null;
  const middleWaypoints = waypoints.length > 2 ? waypoints.slice(1, -1) : [];

  dispatch(setAllWaypoints({ startWaypoint, middleWaypoints, endWaypoint }));

  dispatch(setCorridorWidth(saved.corridorWidthKm));
  const savedPreferences = {
    ...DEFAULT_ROUTE_PREFERENCES,
    ...(saved.routePreferences || {}),
  };
  for (const key of Object.keys(DEFAULT_ROUTE_PREFERENCES)) {
    const value = savedPreferences[key];
    dispatch(setRoutePreference(key, value));
  }
  dispatch(clearItinerary());
  for (const stop of saved.itinerary || []) {
    dispatch(addItineraryStop({ ...stop }));
  }

  dispatch(setCurrentRouteId(saved.id));
  // A previously opened share link leaves `sharedRouteName` set; loading a real
  // saved route supersedes it, so clear it to stop the Save dialog from
  // pre-filling a stale shared name once this route's own name no longer wins.
  dispatch(setSharedRouteName(null));

  // Try the polyline cache. On hit we can skip Directions and show the route
  // immediately; on miss the waypoint-change effect inside
  // useMapboxDirections picks it up on the next render tick.
  const cached = readPolylineCache(saved.id);
  if (cached) {
    dispatch(fetchRouteSuccess(cached.routeGeoJSON, cached.totalDistanceKm, cached.totalDurationMin));
  }
};

/**
 * Decode a shared route from a `?r=` param and load it into the working
 * state — same slot-model rebuild as `loadSavedRoute`, but the route is not
 * (yet) in the profile so `currentRouteId` stays null. The recipient can then
 * use the normal Save flow to persist it. Geometry recomputes via
 * `useMapboxDirections`. On any decode/shape failure we flag `sharedRouteError`
 * so the page can show a non-blocking notice.
 *
 * @param {string} param
 * @returns {import('../../../types/vansite').AppThunkAction<void>}
 */
export const loadSharedRoute = (param) => (dispatch) => {
  const raw = decodeRouteFromParam(param);
  const migrated = raw ? migrateRoute(raw) : null;
  // `validateRoute` is the pre-persist check and requires an `id`, which a
  // freshly decoded share payload doesn't have — so we guard on waypoints here
  // and let the Save flow mint the id + run validateRoute later. The upper
  // bound matters because the param is untrusted: a hand-crafted link with
  // more than MAX_WAYPOINTS coords would sail past the builder's own cap and
  // then make `fetchDirections` throw ("at most 12 coordinates"), leaving the
  // recipient with a permanent route-fetch error instead of a clean notice.
  if (
    !migrated ||
    !Array.isArray(migrated.waypoints) ||
    migrated.waypoints.length < 2 ||
    migrated.waypoints.length > MAX_WAYPOINTS
  ) {
    dispatch(setSharedRouteError(true));
    return;
  }

  const waypoints = migrated.waypoints;
  const startWaypoint = waypoints[0] || null;
  const endWaypoint = waypoints.length >= 2 ? waypoints[waypoints.length - 1] : null;
  const middleWaypoints = waypoints.length > 2 ? waypoints.slice(1, -1) : [];

  dispatch(setAllWaypoints({ startWaypoint, middleWaypoints, endWaypoint }));
  dispatch(setCorridorWidth(migrated.corridorWidthKm));
  const prefs = { ...DEFAULT_ROUTE_PREFERENCES, ...(migrated.routePreferences || {}) };
  for (const key of Object.keys(DEFAULT_ROUTE_PREFERENCES)) {
    dispatch(setRoutePreference(key, prefs[key]));
  }
  dispatch(clearItinerary());
  for (const stop of migrated.itinerary || []) {
    dispatch(addItineraryStop({ ...stop }));
  }
  dispatch(setCurrentRouteId(null));
  dispatch(setSharedRouteName(migrated.name || null));
  dispatch(setSharedRouteError(false));
};

/**
 * Remove a saved route from the profile, mirror the change into Redux, and
 * evict the cached polyline so a future route with the same id would not
 * accidentally hydrate from stale geometry.
 */
export const deleteSavedRoute = (routeId) => async (dispatch, getState, sdk) => {
  const state = getState().RoutePlannerPage;
  const remaining = (state.savedRoutes || []).filter((r) => r.id !== routeId);

  dispatch(saveRouteRequest());
  try {
    const response = await sdk.currentUser.updateProfile(
      { privateData: { savedRoutes: remaining } },
      { expand: true, include: ['profileImage'] },
    );
    const updatedUser = response?.data?.data;
    if (updatedUser) {
      dispatch(currentUserShowSuccess(updatedUser));
    }
    dispatch(setSavedRoutes(remaining));
    dispatch(saveRouteSuccess());
    removePolylineCache(routeId);
    if (getState().RoutePlannerPage.currentRouteId === routeId) {
      dispatch(setCurrentRouteId(null));
    }
  } catch (error) {
    dispatch(saveRouteError(storableError(error)));
    // Refresh from server to avoid a divergent local state.
    dispatch(fetchCurrentUser());
  }
};

/**
 * Populate the polyline cache after a fresh Directions fetch. Wired from the
 * `useMapboxDirections` hook so a loaded saved route gets its cache backfilled
 * the moment Mapbox hands us the geometry.
 */
export const cacheCurrentRoutePolyline = () => (_dispatch, getState) => {
  const state = getState().RoutePlannerPage;
  if (state.currentRouteId && state.routeGeoJSON) {
    writePolylineCache(state.currentRouteId, {
      routeGeoJSON: state.routeGeoJSON,
      totalDistanceKm: state.totalDistanceKm,
      totalDurationMin: state.totalDurationMin,
    });
  }
};

/**
 * Fetch full listing records for every stop currently in the itinerary and
 * merge them into marketplaceData. Needed because `fetchCorridorListings`
 * only loads listings that fall in the corridor bbox at fetch time — a
 * saved route reloaded in a later session, or a Trip Mode view opened
 * before the corridor query has finished, would otherwise render stop
 * cards without images or titles.
 *
 * Chunked at 100 (Sharetribe's per-page cap) via `ids` filter.
 *
 * @returns {import('../../../types/vansite').AppThunkAction<Promise<void>>}
 */
export const fetchItineraryListings = () => async (dispatch, getState, sdk) => {
  const state = getState().RoutePlannerPage;
  const itinerary = state.itinerary || [];
  if (itinerary.length === 0) return;

  // Skip listings we already have denormalized — keeps the reload path cheap
  // when the user flips Plan/Trip tabs after the corridor query has loaded.
  const entities = getState().marketplaceData?.entities?.listing || {};
  const missingIds = [];
  for (const stop of itinerary) {
    if (!stop.listingId) continue;
    if (!entities[stop.listingId]) missingIds.push(stop.listingId);
  }
  if (missingIds.length === 0) return;

  const chunks = [];
  for (let i = 0; i < missingIds.length; i += LISTINGS_PER_TILE) {
    chunks.push(missingIds.slice(i, i + LISTINGS_PER_TILE));
  }

  try {
    const responses = await Promise.all(
      chunks.map((ids) =>
        sdk.listings.query({
          ids: ids.join(','),
          per_page: LISTINGS_PER_TILE,
          ...LISTINGS_QUERY_FIELDS,
        }),
      ),
    );
    for (const response of responses) {
      dispatch(addMarketplaceEntities(response));
    }
  } catch (error) {
    // Non-fatal — stop cards will keep their no-image placeholders. Swallow
    // rather than dispatching an error state the user can't meaningfully
    // act on.
  }
};

// Cap on transaction pages walked when hydrating Trip Mode. The `only: 'order'`
// + accepted-transitions filter already trims to the active bookings for the
// current user — exceeding 3 pages (300 bookings) would be extraordinary, and
// the safety cap keeps a pathological account from spinning the map forever.
const BOOKED_QUERY_PAGE_LIMIT = 3;
const BOOKED_QUERY_PER_PAGE = 100;

/**
 * Hydrate `bookedListingMap` from the current user's accepted orders.
 *
 * Runs on Trip Mode entry (or itinerary change while in Trip Mode). Walks up
 * to `BOOKED_QUERY_PAGE_LIMIT` pages of the user's orders filtered to the
 * accepted / auto-accepted / confirm-payment transitions — that's how we
 * mirror the "reserved" definition used in `fetchCurrentUserHasOrders`. No
 * server-side filter by listingId: Sharetribe's transactions.query doesn't
 * support that, and the result set is already scoped to the current user's
 * active orders so the walk is cheap.
 *
 * @returns {import('../../../types/vansite').AppThunkAction<Promise<void>>}
 */
export const detectBookedListings = () => async (dispatch, getState, sdk) => {
  const isAuthenticated = getState().auth?.isAuthenticated;
  if (!isAuthenticated) {
    // Anonymous users have no transactions; clear any stale map from a prior
    // logged-in session and short-circuit.
    dispatch(fetchBookedSuccess({}));
    return;
  }

  dispatch(fetchBookedRequest());
  try {
    const allTxs = [];
    const allIncluded = [];
    for (let page = 1; page <= BOOKED_QUERY_PAGE_LIMIT; page += 1) {
       
      const response = await sdk.transactions.query({
        only: 'order',
        page,
        per_page: BOOKED_QUERY_PER_PAGE,
        include: ['booking', 'listing'],
        lastTransitions: BOOKED_TRANSITIONS,
      });
      const data = response?.data?.data ?? [];
      const included = response?.data?.included ?? [];
      allTxs.push(...data);
      allIncluded.push(...included);
      if (data.length < BOOKED_QUERY_PER_PAGE) break;
    }
    dispatch(fetchBookedSuccess(buildBookedMap(allTxs, allIncluded)));
  } catch (error) {
    dispatch(fetchBookedError(storableError(error)));
  }
};
